/* eslint-disable @typescript-eslint/no-explicit-any */

type LogLevel = 'INFO' | 'WARN' | 'ERROR' | 'DEBUG';

interface LogEntry {
  ts: number;
  level: LogLevel;
  tag: string;
  message?: string;
  data?: any;
}

export interface ExportLogger {
  setRequestId(id: string): void;
  log(level: LogLevel, tag: string, message?: string, data?: any): void;
  attach(tag: string, data: any): void;
  finalize(outcome: 'success' | 'error' | 'cancel', extra?: any): void;
  getContent(): string;
  download(filename?: string): void;
}

function tsString(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${String(d.getMilliseconds()).padStart(3, '0')}`;
}

export function createExportLogger(initial?: { requestId?: string }): ExportLogger {
  const entries: LogEntry[] = [];
  let reqId = initial?.requestId || '';

  function push(level: LogLevel, tag: string, message?: string, data?: any) {
    entries.push({ ts: Date.now(), level, tag, message, data });
  }

  return {
    setRequestId(id: string) {
      reqId = id;
      push('DEBUG', 'logger.requestId', 'set', { requestId: id });
    },
    log(level: LogLevel, tag: string, message?: string, data?: any) {
      push(level, tag, message, data);
    },
    attach(tag: string, data: any) {
      push('DEBUG', tag, undefined, data);
    },
    finalize(outcome: 'success' | 'error' | 'cancel', extra?: any) {
      push(outcome === 'success' ? 'INFO' : outcome === 'cancel' ? 'WARN' : 'ERROR', 'logger.finalize', outcome, extra);
    },
    getContent(): string {
      const header = [`Export Flow Log`, `requestId=${reqId || 'n/a'}`, `entries=${entries.length}`, ''].join('\n');
      const lines = entries.map((e) => {
        const base = `[${tsString(e.ts)}] ${e.level} ${e.tag}`;
        const msg = e.message ? ` - ${e.message}` : '';
        const data = typeof e.data !== 'undefined' ? ` | ${safeStringify(e.data)}` : '';
        return base + msg + data;
      });
      return header + lines.join('\n');
    },
    download(filename?: string) {
      try {
        const name = filename || `export_flow_${reqId || Date.now()}.log`;
        const content = this.getContent();
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch {
        /* noop */
      }
    },
  };
}

function safeStringify(value: any): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    try {
      return String(value);
    } catch {
      return '[unserializable]';
    }
  }
}


